import React, { useState } from "react";
import { Home, Globe, Power } from "lucide-react";

const menu = [
  { title: "Home", icon: <Home size={24} />, path: "/" },
  { title: "Discover", icon: <Globe size={24} />, path: "/discover" },
];

const SideBar = ({ mode = "user", onLogout, isDarkMode = true, onToggleTheme, onLinkClick, currentPath }) => {
  const [sidebarOpen] = useState(false);

  // Theme configuration for sidebar
  const sidebarTheme = {
    dark: {
      bg: 'bg-black',
      border: 'border-gray-800',
      logoBg: '#000000',
      hover: 'hover:bg-gray-900',
      buttonBg: 'bg-gray-900',
      buttonHover: 'hover:bg-red-600',
      text: 'text-gray-300',
      textHover: 'hover:text-white',
      themeButton: 'bg-gray-900 hover:bg-gray-800',
      themeButtonIcon: 'text-yellow-400',
    },
    light: {
      bg: 'bg-[#f6f6f1]',
      border: 'border-[#f6f6f1]',
      logoBg: '#f6f6f1',
      hover: 'hover:bg-gray-100',
      buttonBg: 'bg-white',
      buttonHover: 'hover:bg-red-100',
      text: 'text-gray-600',
      textHover: 'hover:text-gray-900',
      themeButton: 'bg-white hover:bg-gray-50',
      themeButtonIcon: 'text-gray-600',
    }
  };
  
  const currentSidebarTheme = isDarkMode ? sidebarTheme.dark : sidebarTheme.light;

  // For agent mode, use theme-aware design
  if (mode === "agent") {
    return (
      <aside className={`relative z-[60] flex flex-col ${currentSidebarTheme.bg} min-w-[72px] ${sidebarOpen ? 'w-[288px]' : 'w-[72px]'} py-4 ${currentSidebarTheme.border} border-r h-screen transition-all duration-300 ease-in-out`} style={{ minWidth: sidebarOpen ? 288 : 72, width: sidebarOpen ? 288 : 72 }}>
        {/* Top: Logo */}
        <div
          className="mb-2 flex flex-col items-center justify-center w-full transition-colors duration-300"
          style={{
            zIndex: 30,
            position: "relative",
            background: currentSidebarTheme.logoBg
          }}
        >
          <img
            src="/A_bgless.png"
            alt="Arta Logo"
            style={{ display: 'block', margin: '0 auto', maxWidth: '40px', height: 'auto' }}
          />
        </div>

        {/* Middle: Nav */}
        {/* <nav className="flex flex-col items-center gap-2 mt-6 flex-1">
          {menu.map((item, idx) => (
            <a
              key={item.title}
              href={item.path}
              className="group relative flex items-center justify-center w-12 h-12 my-1 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition"
            >
              {item.icon}
            </a>
          ))}
        </nav> */}
        <div className="flex-1" />

        {/* Bottom buttons container */}
        <div className="flex flex-col items-center gap-3 absolute left-1/2 -translate-x-1/2 bottom-6" style={{ zIndex: 50 }}>
          {/* Copilot Dashboard Button - Only show on AgentDashboard, not on TSECopilotDashboard */}
          {mode === "agent" && currentPath !== "/TseDashboard" && (
            <button
              className={`${currentSidebarTheme.buttonBg} hover:bg-purple-100 text-purple-500 hover:text-purple-700 rounded-full w-10 h-10 flex items-center justify-center shadow ${currentSidebarTheme.border} border transition-all duration-300`}
              title="Open TSE Copilot Dashboard"
              onClick={() => {
                const tseDashboardUrl = `${window.location.origin}/TseDashboard`;
                window.open(tseDashboardUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
              }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </button>
          )}
          
          {/* Theme Toggle Button - Only show on AgentDashboard, not on TSECopilotDashboard */}
          {onToggleTheme && mode === "agent" && currentPath !== "/TseDashboard" && (
            <button
              className={`${currentSidebarTheme.themeButton} rounded-full w-10 h-10 flex items-center justify-center shadow ${currentSidebarTheme.border} border transition-all duration-300`}
              title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              onClick={onToggleTheme}
            >
              {isDarkMode ? (
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className={currentSidebarTheme.themeButtonIcon}>
                  <circle cx="12" cy="12" r="5"/>
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                </svg>
              ) : (
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className={currentSidebarTheme.themeButtonIcon}>
                  <path d="M21 12.79A9 9 0 1 1 11.21 3A7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
          )}
          
          {/* Logout Button */}
          <button
            className={`${currentSidebarTheme.buttonBg} ${currentSidebarTheme.buttonHover} text-red-400 hover:text-white rounded-full w-10 h-10 flex items-center justify-center shadow ${currentSidebarTheme.border} border transition-all duration-300`}
            title="Logout"
            onClick={onLogout}
          >
            <Power size={22} />
          </button>
        </div>

        {/* Slide-out panel (empty for now) */}
        <div
          className={`absolute left-0 top-[80px] h-[calc(100%-80px)] ${currentSidebarTheme.bg} ${currentSidebarTheme.border} border-r shadow-lg z-20 transition-all duration-300 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
          style={{ width: 288, minWidth: 288, pointerEvents: sidebarOpen ? "auto" : "none" }}
        >
          {/* Empty expanded panel for agent mode */}
        </div>
      </aside>
    );
  }

  // Default: user sidebar (existing content)
  return (
    <aside className="relative z-[60] flex flex-col justify-between items-center bg-[#F3F3ED] min-w-[72px] w-[72px] py-4 border-r border-[#F3F3ED] h-screen">
      {/* Top: Logo + New Chat */}
      <div className="flex flex-col gap-6 items-center">
        {/* Logo */}
        <div className="mb-2 flex items-center justify-center w-full">
          <img
            src="/A_bgless.png"
            alt="Arta Logo"
            style={{ display: 'block', margin: '0 auto', maxWidth: '40px', height: 'auto' }}
          />
        </div>
      </div>

      {/* Middle: Nav */}
      <nav className="flex flex-col items-center gap-2 mt-6 flex-1">
        {menu.map((item) => (
          <button
            key={item.title}
            onClick={() => onLinkClick && onLinkClick(item.path)}
            title={item.title}
            className="group relative flex items-center justify-center w-12 h-12 my-1 rounded-lg hover:bg-[#e6f0ef] transition"
          >
            {item.icon}
          </button>
        ))}
      </nav>

      {/* Bottom: Account, Upgrade, Install, Power-off Logout Button */}
      <div className="flex flex-col gap-3 items-center mb-2">
        <button
          className="bg-white hover:bg-red-100 rounded-full w-10 h-10 flex items-center justify-center shadow border border-[#EBECE6] transition"
          title="Logout"
          onClick={onLogout}
          style={{ color: '#e53e3e' }}
        >
          <Power size={22} />
        </button>
      </div>
    </aside>
  );
};

export default SideBar;
